// ════════════════════════════════════════════════════════════════════
// LYRAEA — ArtistPage.js
// ────────────────────────────────────────────────────────────────────
// An artist here is not a string on a track — it is a MARK. The same
// identity that walks the Pier, holds Crests and carries deeds. Tap a
// name anywhere in Lyraea and you land on the person, not a text match.
//
// This is the payoff of one shared profile: Lyraea did not have to invent
// artist accounts, artist bios, artist links or artist avatars. It reads
// profiles/{uid}/mark — the same node the Pier's Mark card reads — and the
// artist edits it in ONE place for the whole Empire.
//
// ── WHY THE FULL MARK IS NOT RENDERED HERE ───────────────────────────
// The full card also shows deeds and Standing, which need the achievement
// registry — achievements.js, useAchievements, achievementWrites, the badge
// book. That chain belongs to the engine, and copying four files into this
// app to show two numbers would fork the registry across repos, which the
// Registry Sync Law exists to prevent. So Lyraea renders the IDENTITY half
// (code, seal, tagline, links, shop) and hands off to the Pier for the rest.
// When the engine ships as a package, this becomes the full card and this
// comment goes away.
// ════════════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { useMark, accentOf, fmtSince, fmtNum } from "./markData";
import { fmtPlays, fmtTime } from "./lyraeaData";
import { Cover } from "./Player";
import { AlbumRail } from "./AlbumPage";

const C = {
  void: "var(--void)", obsidian: "var(--obsidian)", slab: "var(--slab)",
  slabHi: "var(--slab-hi)", edge: "var(--edge)", edgeHot: "var(--edge-hot)",
  gold: "var(--gold)", goldHi: "var(--gold-hi)", violet: "var(--violet)",
  bone: "var(--bone)", ash: "var(--ash)", smoke: "var(--smoke)", verd: "var(--verd)",
  display: "var(--display)", mono: "var(--mono)", sans: "var(--sans)",
};

const PIER_URL = "https://unstable-pierre.pages.dev";

const Kicker = ({ children, tint = C.smoke, size = 9 }) => (
  <div style={{
    fontFamily: C.mono, fontSize: size, letterSpacing: ".28em",
    textTransform: "uppercase", color: tint,
  }}>{children}</div>
);

const Stat = ({ label, value, tint = C.bone }) => (
  <div>
    <Kicker size={8}>{label}</Kicker>
    <div style={{ fontFamily: C.display, fontWeight: 600, fontSize: 20, color: tint, marginTop: 3 }}>
      {value}
    </div>
  </div>
);

export default function ArtistPage({
  artistUid, tracks, albums = [], onBack, onPlay, onPlayAll,
  onOpenAlbum, current, playing,
}) {
  const m = useMark(artistUid);
  const accent = accentOf(m.mark);

  const mine = useMemo(
    () => tracks.filter((t) => t.artistUid === artistUid),
    [tracks, artistUid]
  );

  const totalPlays = useMemo(
    () => mine.reduce((n, t) => n + Number(t.plays || 0), 0),
    [mine]
  );

  // The displayed name prefers the Mark's own name, and falls back to
  // whatever the tracks are credited to — an artist who has never set a
  // display name still reads correctly instead of showing "UNCLAIMED".
  const name = m.profile
    ? (m.name !== "UNCLAIMED" ? m.name : (mine[0]?.artist || "Unknown"))
    : (mine[0]?.artist || "Unknown");

  const links = Object.entries(m.mark?.links || {});
  const store = (m.storeItems || []).filter((i) => i.kind !== "track");

  return (
    <div style={{ animation: "lyFade .25s both" }}>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: C.ash, padding: "2px 0 12px",
        fontFamily: C.mono, fontSize: 10, letterSpacing: ".26em",
      }}>← BACK</button>

      {/* ── THE HEADER ────────────────────────────────────────── */}
      <div style={{
        position: "relative", overflow: "hidden", marginBottom: 18,
        background: `linear-gradient(150deg, ${C.slabHi}, ${C.obsidian} 60%, ${C.void})`,
        border: `1px solid ${C.edge}`,
        boxShadow: `0 0 40px ${accent.hex}14`,
        clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))",
        padding: 16,
      }}>
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `radial-gradient(ellipse at 10% 0%, ${accent.hex}26, transparent 58%)`,
        }} />

        <div style={{ position: "relative", display: "flex", gap: 13, alignItems: "flex-start" }}>
          <div style={{
            width: 62, height: 62, flex: "0 0 auto", display: "grid", placeItems: "center",
            background: `linear-gradient(150deg, ${C.slabHi}, ${C.obsidian})`,
            border: `1px solid ${accent.hex}55`,
            boxShadow: `inset 0 0 30px ${accent.hex}18`,
            clipPath: "polygon(0 0, calc(100% - 9px) 0, 100% 9px, 100% 100%, 9px 100%, 0 calc(100% - 9px))",
            fontFamily: C.display, fontSize: 26, color: accent.hex,
          }}>{m.seal}</div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Kicker size={8.5} tint={accent.hex}>THE MARK</Kicker>
              <span style={{ fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".18em", color: C.smoke }}>
                {m.code}
              </span>
            </div>
            <div style={{
              fontFamily: C.display, fontWeight: 800, fontSize: 27, color: C.bone,
              lineHeight: 1.08, marginTop: 3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{name}</div>
            {m.mark?.tagline && (
              <div style={{ fontFamily: C.sans, fontSize: 14, color: C.ash, marginTop: 5, lineHeight: 1.35 }}>
                {m.mark.tagline}
              </div>
            )}
          </div>
        </div>

        <div style={{
          position: "relative", display: "grid", gap: 12, marginTop: 15,
          gridTemplateColumns: "repeat(auto-fit, minmax(74px, 1fr))",
        }}>
          <Stat label="TRACKS" value={fmtNum(mine.length)} />
          <Stat label="PLAYS" value={fmtPlays(totalPlays)} />
          <Stat label="CRESTS" value={`◆ ${fmtNum(m.crests)}`} tint={accent.hi} />
          <Stat label="MARKED" value={fmtSince(m.since)} />
        </div>

        <div style={{ position: "relative", display: "flex", gap: 8, marginTop: 15, flexWrap: "wrap" }}>
          <button
            onClick={() => onPlayAll(mine.filter((t) => !t.priceDC))}
            disabled={!mine.some((t) => !t.priceDC)}
            style={{
              background: `linear-gradient(150deg, ${accent.hi}, ${accent.hex})`,
              border: "none", color: C.void, fontFamily: C.mono, fontSize: 9.5,
              letterSpacing: ".22em", padding: "10px 16px",
              opacity: mine.some((t) => !t.priceDC) ? 1 : .4,
            }}>▶ PLAY ALL</button>

          <a href={`${PIER_URL}?uid=${encodeURIComponent(artistUid)}`}
            style={{
              fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".2em", color: C.smoke,
              textDecoration: "none", border: `1px solid ${C.edge}`, padding: "10px 13px",
            }}>FULL MARK ↗</a>

          {links.map(([label, url]) => (
            <a key={label} href={url} target="_blank" rel="noopener noreferrer" style={{
              fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".18em", color: C.smoke,
              textDecoration: "none", border: `1px solid ${C.edge}`, padding: "10px 13px",
            }}>{label.toUpperCase()} ↗</a>
          ))}
        </div>
      </div>

      {/* ── THEIR ALBUMS ──────────────────────────────────────── */}
      {onOpenAlbum && (
        <AlbumRail
          albums={albums.filter((al) => al.artistUid === artistUid)}
          tracks={tracks}
          onOpen={onOpenAlbum}
          label="ALBUMS"
        />
      )}

      {/* ── THEIR TRACKS ──────────────────────────────────────── */}
      <Kicker>TRACKS</Kicker>
      <div style={{ height: 8 }} />
      {mine.length === 0 && (
        <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, padding: "18px 2px" }}>
          Nothing published under this Mark yet.
        </div>
      )}
      {mine.map((t, i) => (
        <div key={t.id}
          onClick={() => !t.priceDC && onPlay(t, mine.filter((x) => !x.priceDC))}
          style={{
            display: "flex", alignItems: "center", gap: 11, padding: "9px 6px",
            borderBottom: `1px solid ${C.edge}`,
            cursor: t.priceDC ? "default" : "pointer",
            opacity: t.priceDC ? .55 : 1,
            background: current === t.id ? `${C.violet}12` : "transparent",
          }}>
          <span style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke, width: 20 }}>
            {current === t.id ? "◆" : String(i + 1).padStart(2, "0")}
          </span>
          <Cover src={t.cover} seed={t.id} title={t.title} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: C.sans, fontSize: 15.5, color: current === t.id ? C.goldHi : C.bone,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{t.title}</div>
            <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".12em", color: C.smoke }}>
              {fmtPlays(t.plays)} PLAYS
            </div>
          </div>
          <span style={{ fontFamily: C.mono, fontSize: 10, color: t.priceDC ? C.gold : C.smoke }}>
            {t.priceDC ? `◆ ${t.priceDC}` : fmtTime(t.durationSec)}
          </span>
        </div>
      ))}

      {/* ── WHAT ELSE THEY HAVE — the Mark's shop window ──────── */}
      {store.length > 0 && (
        <>
          <div style={{ height: 22 }} />
          <Kicker tint={C.verd}>ALSO FOR SALE</Kicker>
          <div style={{ height: 8 }} />
          {store.map((it) => (
            <div key={it.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 6px",
              borderBottom: `1px solid ${C.edge}`,
            }}>
              <Cover src={it.cover} seed={it.id} title={it.title} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: C.sans, fontSize: 14.5, color: C.bone }}>{it.title}</div>
                <div style={{ fontFamily: C.mono, fontSize: 8.5, letterSpacing: ".18em", color: C.smoke, textTransform: "uppercase" }}>
                  {it.kind}
                </div>
              </div>
              <span style={{ fontFamily: C.mono, fontSize: 10, color: it.priceDC ? C.gold : C.verd }}>
                {it.priceDC ? `◆ ${it.priceDC}` : "FREE"}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
